import {
  EMBEDDING_PROFILES,
  firstUseDownloadBytes,
  formatBytes,
} from '../data/embeddingProfiles'
import type { ModelTier } from '../domain/types'
import type { SemanticAnalysisController } from '../embedding/useSemanticAnalysis'

type StatusAction = {
  label: 'Pause' | 'Set up' | 'Retry'
  run: () => void
}

type StatusPresentation = {
  tone: 'setup' | 'preview' | 'ready' | 'paused' | 'error'
  emoji: string
  title: string
  detail: string
  busy: boolean
  announce: string
  iconMotion?: 'spin'
  action?: StatusAction
  progress?: {
    value?: number
  }
}

export function semanticStatusPresentation(
  controller: SemanticAnalysisController,
  hasPrompt: boolean,
  currentPrompt?: string,
  currentRoute?: ModelTier,
): StatusPresentation | null {
  const { runtime } = controller
  const analysis =
    controller.analysis.phase === 'complete' &&
    currentPrompt !== undefined &&
    controller.analysis.prompt !== currentPrompt
      ? {
          phase: 'waiting' as const,
          revision: controller.analysis.revision + 1,
        }
      : controller.analysis
  const profile = EMBEDDING_PROFILES[controller.profileId]
  const setupSize = formatBytes(firstUseDownloadBytes(profile))
  const previewDetail =
    'This preview may choose a larger model and use more tokens.'

  if (runtime.phase === 'idle') return null

  if (runtime.phase === 'scheduled') {
    return {
      tone: hasPrompt ? 'preview' : 'setup',
      emoji: hasPrompt ? '🛡️' : '⬇️',
      title: hasPrompt
        ? 'Conservative preview'
        : 'Local check',
      detail: hasPrompt
        ? `Local setup starts shortly. ${previewDetail}`
        : `Setup starts shortly · approximately ${setupSize} first use.`,
      busy: true,
      announce: 'Local model setup scheduled.',
      action: {
        label: 'Pause',
        run: controller.pauseSetup,
      },
    }
  }

  if (runtime.phase === 'loading') {
    const percent =
      runtime.progress.status === 'progress_total'
        ? Math.round(runtime.progress.progress ?? 0)
        : null
    const setupDetail =
      percent === null
        ? runtime.progress.phase === 'runtime'
          ? 'Preparing browser runtime…'
          : 'Downloading local model…'
        : `${percent}% · approximately ${setupSize} first use`
    return {
      tone: hasPrompt ? 'preview' : 'setup',
      emoji: hasPrompt ? '🛡️' : '⬇️',
      title: hasPrompt
        ? 'Conservative preview'
        : 'Setting up local check',
      detail: hasPrompt
        ? `${runtime.fallback ? 'Using a compatible browser runtime. ' : `${setupDetail}. `}${previewDetail}`
        : runtime.fallback
          ? 'Using a compatible browser runtime.'
          : setupDetail,
      busy: true,
      announce: 'Local model setup started.',
      action: {
        label: 'Pause',
        run: controller.pauseSetup,
      },
      progress: {
        value: percent ?? undefined,
      },
    }
  }

  if (runtime.phase === 'deferred') {
    const reasonCopy = {
      hidden: 'Setup resumes when this tab is active.',
      offline: 'Setup resumes when you reconnect.',
      'save-data': `Data Saver paused the approximately ${setupSize} setup.`,
      'user-paused': 'Automatic setup is paused for this session.',
    }[runtime.reason]
    return {
      tone: hasPrompt ? 'preview' : 'paused',
      emoji: hasPrompt ? '🛡️' : '⏸️',
      title: hasPrompt
        ? 'Conservative preview'
        : 'Local check paused',
      detail: `${reasonCopy}${hasPrompt ? ` ${previewDetail}` : ''}`,
      busy: false,
      announce: 'Local model setup paused.',
      action:
        runtime.reason === 'save-data' ||
        runtime.reason === 'user-paused'
          ? {
              label: 'Set up',
              run: controller.retrySetup,
            }
          : undefined,
    }
  }

  if (runtime.phase === 'error') {
    return {
      tone: hasPrompt ? 'preview' : 'error',
      emoji: '⚠️',
      title: hasPrompt
        ? 'Conservative preview'
        : 'Local check unavailable',
      detail: hasPrompt
        ? `Local setup stopped. ${previewDetail}`
        : runtime.retryable
          ? 'Local setup stopped. You can try again.'
          : 'This browser cannot run the local check.',
      busy: false,
      announce:
        'Local model setup failed. Preview remains available.',
      action: runtime.retryable
        ? {
            label: 'Retry',
            run: controller.retrySetup,
          }
        : undefined,
    }
  }

  if (!hasPrompt || analysis.phase === 'idle') {
    return {
      tone: 'ready',
      emoji: '✅',
      title: 'Local check ready',
      detail: `${runtime.device} runtime · type a prompt to use it.`,
      busy: false,
      announce: 'Local model ready.',
    }
  }

  if (
    analysis.phase === 'waiting' ||
    analysis.phase === 'running'
  ) {
    return {
      tone: 'preview',
      emoji: '↻',
      title: 'Updating route',
      detail:
        analysis.phase === 'running'
          ? 'Comparing the current prompt locally…'
          : 'Checking the current prompt locally…',
      busy: true,
      announce: 'Updating route.',
      iconMotion: 'spin',
    }
  }

  if (analysis.phase === 'error') {
    return {
      tone: 'preview',
      emoji: '⚠️',
      title: 'Conservative preview',
      detail: `Could not check this prompt. ${previewDetail}`,
      busy: false,
      announce:
        'Local prompt check failed. Preview remains available.',
    }
  }

  return {
    tone: 'ready',
    emoji: '✅',
    title: 'Local check complete',
    detail: `Evidence added · ${analysis.device} runtime.`,
    busy: false,
    announce: currentRoute
      ? `Local check complete. ${currentRoute} route selected.`
      : 'Local evidence added.',
  }
}

export function SemanticStatus({
  controller,
  hasPrompt,
  currentPrompt,
  currentRoute,
}: {
  controller: SemanticAnalysisController
  hasPrompt: boolean
  currentPrompt?: string
  currentRoute?: ModelTier
}) {
  const presentation = semanticStatusPresentation(
    controller,
    hasPrompt,
    currentPrompt,
    currentRoute,
  )
  if (!presentation) return null

  return (
    <section
      className={`semantic-strip semantic-strip--${presentation.tone}`}
      aria-label="Local analysis status"
      aria-busy={presentation.busy}
    >
      <span
        className={`semantic-strip__icon${
          presentation.iconMotion === 'spin'
            ? ' semantic-strip__icon--spin'
            : ''
        }`}
        aria-hidden="true"
      >
        {presentation.emoji}
      </span>
      <p>
        <strong>{presentation.title}</strong>
        <span>{presentation.detail}</span>
      </p>
      {presentation.progress ? (
        <progress
          aria-label="Local model setup progress"
          max={100}
          value={presentation.progress.value}
        />
      ) : null}
      {presentation.action ? (
        <button
          className="semantic-strip__action"
          type="button"
          onClick={presentation.action.run}
        >
          {presentation.action.label}
        </button>
      ) : null}
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {presentation.announce}
      </span>
    </section>
  )
}
